import React, { useRef, useCallback } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Link, AlignLeft, AlignCenter, Type, Palette } from 'lucide-react';

export default function RichTextEditor({ value, onChange, placeholder = '' }) {
  const editorRef = useRef(null);

  const exec = useCallback((cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    if (onChange) onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const handleInput = useCallback(() => {
    if (onChange) onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const insertLink = () => {
    const url = prompt('URL del enlace:');
    if (url) exec('createLink', url);
  };

  const setColor = (color) => exec('foreColor', color);

  const ToolBtn = ({ onClick, children, title }) => (
    <button type="button" onMouseDown={e => { e.preventDefault(); onClick(); }} title={title}
      className="p-1.5 rounded hover:bg-white/10 text-[#9ca3af] hover:text-purple-300 transition-colors">
      {children}
    </button>
  );

  return (
    <div className="rounded-lg overflow-hidden border border-gold-300/10" data-testid="rich-text-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-[rgba(20,10,35,0.8)] border-b border-purple-400/15">
        <select onChange={e => { if (e.target.value) exec('formatBlock', e.target.value); e.target.value = ''; }}
          className="bg-transparent text-[#9ca3af] text-xs border border-white/10 rounded px-1.5 py-1 mr-1 cursor-pointer">
          <option value="">Formato</option>
          <option value="h1">Titulo 1</option>
          <option value="h2">Titulo 2</option>
          <option value="h3">Titulo 3</option>
          <option value="p">Parrafo</option>
        </select>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <ToolBtn onClick={() => exec('bold')} title="Negrita"><Bold size={15} /></ToolBtn>
        <ToolBtn onClick={() => exec('italic')} title="Cursiva"><Italic size={15} /></ToolBtn>
        <ToolBtn onClick={() => exec('underline')} title="Subrayado"><Underline size={15} /></ToolBtn>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <ToolBtn onClick={() => setColor('#c084fc')} title="Purpura"><Palette size={15} className="text-purple-400" /></ToolBtn>
        <ToolBtn onClick={() => setColor('#e6c36a')} title="Dorado"><Palette size={15} className="text-yellow-400" /></ToolBtn>
        <ToolBtn onClick={() => setColor('#d1d5db')} title="Default"><Type size={15} /></ToolBtn>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <ToolBtn onClick={() => exec('insertUnorderedList')} title="Lista"><List size={15} /></ToolBtn>
        <ToolBtn onClick={() => exec('insertOrderedList')} title="Lista numerada"><ListOrdered size={15} /></ToolBtn>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <ToolBtn onClick={() => exec('justifyLeft')} title="Alinear izquierda"><AlignLeft size={15} /></ToolBtn>
        <ToolBtn onClick={() => exec('justifyCenter')} title="Centrar"><AlignCenter size={15} /></ToolBtn>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <ToolBtn onClick={insertLink} title="Enlace"><Link size={15} /></ToolBtn>
      </div>
      {/* Editor */}
      <div ref={editorRef} contentEditable suppressContentEditableWarning
        onInput={handleInput}
        data-placeholder={placeholder}
        className="min-h-[200px] px-4 py-3 text-sm text-[#d1d5db] leading-relaxed bg-[rgba(10,5,20,0.6)] focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-[#6b7280] empty:before:italic [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-purple-300 [&_h1]:mb-2 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-purple-300 [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-purple-300 [&_h3]:mb-1 [&_a]:text-purple-400 [&_a]:underline [&_ul]:list-disc [&_ul]:ml-5 [&_ol]:list-decimal [&_ol]:ml-5"
        style={{ wordBreak: 'break-word' }}
      />
    </div>
  );
}
